// src/data/editions/compensation.js
//
// WORKERS' COMPENSATION edition: a compensation board or insurer reading the
// workforces it covers, by sector, in the weeks before a mental stress claim
// is registered. Shaped for the WSIB conversation of 2026-11-04 and written
// for any board (WSIB, WCB Alberta, WorkSafeBC), so nothing in it names one.
// The organizational leg is what the board already holds: registrations,
// allowances, lost-time claims, duration, return to work and cost, by sector
// (the compensation pack in data/workforceDictionary.js). The members' leg is
// the private check-in at k >= 5. The console names where the two disagree:
// strain rising where claims have not yet is the early-warning gap, and the
// point of the edition. Nothing here touches a claim decision; the forecast
// footer says so in so many words. DRAFT copy; the modeled example stands in
// until a board's own figures are on file.

const compensation = {
    id: 'compensation',
    label: 'Workers’ Compensation (Board / Insurer)',

    // The console's own palette: wsib.ca's navy and blue with the green from
    // its logo as the accent, by association only.
    palette: { deep: '#003359', blue: '#0D73B8', yellow: '#00A851' },

    // The console as a page: the hub sidebar entry, the console sidebar kicker
    // and the header line. Every tab applies: the forecast and the cost tabs
    // are the reason a board is looking.
    consoleLabel: 'Prevention Insights',
    headerSubtitle: 'Strain by sector in the weeks before a claim, built from private check-ins, the board’s own claims figures and sector references.',

    pageTitles: {
        'cc-overview': 'Prevention & Claims Overview',
        'cc-triangulation': 'Triangulation: Workers, the Claims Record, the Average',
        'cc-data': 'Data Intake: The Board’s Own Figures',
        'cc-ai': 'AI Intelligence & Charting',
        'cc-readiness': 'Strain by Sector',
        'cc-predictive': 'Mental Stress Claims Outlook',
        'cc-roi': 'Claim Cost Avoidance',
        'cc-feedback': 'Anonymous Pulse & Sentiment',
        default: 'Prevention Insights'
    },

    // The "Where it is heading" board's next step, by what leads in a sector
    // (utils/earlyWarning.js reads these over its workplace defaults). A board
    // acts through the sector's employers and the hub, upstream of any claim.
    earlyWarning: {
        steps: {
            anxiety: 'Workload and recovery, before a claim: the workload-and-recovery guide and the manager conversation guide to this sector’s employers this month, through the hub.',
            depression: 'Route to support early: the assistance program and the program of care’s front door in front of this sector now, so a claim is not the first contact.',
            sleep: 'Shift recovery: the sleep content to this sector, and a word with its employers about rest between shifts.',
            focus: 'Focus and attention: the focus-and-recovery content to this sector, and a word with its employers about interruptions, hours and rest across the week.',
            trauma: 'After difficult events: peer support and the after-a-hard-day guide to this sector, and a debrief its employers do not bump; the step before a traumatic mental stress claim.',
            divergence: 'Running Hot looks fine from the outside and is the cohort claims come from later: a manager conversation guide to this sector, and a shorter check-in cadence until it eases.',
            none: 'Hold: keep the cadence and watch the four-week change beside this sector’s claims figures.'
        }
    },

    // Legend label for the second series on the Overview trend chart.
    trendSignalLabel: 'Mental stress claim signal',

    summary: {
        source: 'demo', // drives the console's "Modeled example" badge
        capacityScore: 71,
        capacityTrend: -2,
        workforce: 2400,
        highSignalCohort: 290,
        topPriorityGroup: 'Health Care',
        topPulseTheme: 'Workload',
        participationRate: 58,
        modeledClaimsAvoidance: '$3.4M',
        modeledStdClaims: { low: 6, high: 9, windowDays: 90 },
        modeledLtdTransition: { low: 2, high: 3 },
        leaveProbability: { mid: 15, low: 12, high: 18 },
        outlookTitle: 'Claims Outlook',
        outlookNoun: 'mental stress claims',
        outlookUnit: 'claims',
        financialNoun: 'claim-cost avoidance'
    },

    capacityTrend: [
        { date: '1st', capacity: 74, signal: 14 },
        { date: '4th', capacity: 74, signal: 14 },
        { date: '8th', capacity: 73, signal: 15 },
        { date: '12th', capacity: 73, signal: 16 },
        { date: '16th', capacity: 72, signal: 16 },
        { date: '20th', capacity: 72, signal: 17 },
        { date: '24th', capacity: 71, signal: 18 },
        { date: '28th', capacity: 71, signal: 19 },
        { date: 'Today', capacity: 71, signal: 19 }
    ],

    readiness: {
        scoreLabel: 'Workforce Capacity Score',
        signalSubtext: 'workload + exposure to difficult events',
        groups: [
            { name: 'Health Care', pressure: 81, note: 'Short-staffed shifts; violence at work the most-cited exposure', responses: 310 },
            { name: 'Public Safety', pressure: 78, note: 'Critical-incident exposure; claims already high under presumption', responses: 96 },
            { name: 'Education', pressure: 72, note: 'Strain rising; claims have not followed yet', responses: 240 },
            { name: 'Transportation & Transit', pressure: 69, note: 'Assaults on operators; irregular hours', responses: 142 },
            { name: 'Municipal Services', pressure: 64, note: 'Within normal range', responses: 118 },
            { name: 'Retail & Hospitality', pressure: 61, note: 'High turnover; assistance program rarely used', responses: 230 },
            { name: 'Manufacturing', pressure: 58, note: 'Within normal range', responses: 164 },
            { name: 'Agriculture & Food Processing', pressure: 55, note: 'Within normal range', responses: 4 } // below floor
        ],
        signals: [
            { label: 'Self-reported strain trend', value: '+6%', tier: 'Intermediate', tone: 'amber' },
            { label: 'Mental stress claims registered', value: '+9%', tier: 'Lagging', tone: 'amber' },
            { label: 'Average claim duration', value: '+4 days', tier: 'Lagging', tone: 'amber' },
            { label: 'Return to work within three months', value: 'Steady', tier: 'Lagging', tone: 'emerald' }
        ]
    },

    feedback: {
        scopes: [
            { id: 'org', label: 'All sectors', responses: 480 },
            { id: 'health', label: 'Health Care', responses: 150 },
            { id: 'safety', label: 'Public Safety', responses: 62 },
            { id: 'education', label: 'Education', responses: 118 },
            { id: 'transit', label: 'Transportation & Transit', responses: 70 },
            { id: 'agriculture', label: 'Agriculture & Food Processing', responses: 4 } // below floor -> suppressed
        ],
        submissions: [
            { id: 1, time: '2 hours ago', theme: 'Workload', sentiment: 'risk', scope: 'health',
              text: 'Two nurses short again tonight. We are not unsafe yet, but nobody is coming off this shift rested.' },
            { id: 2, time: '5 hours ago', theme: 'Exposure', sentiment: 'risk', scope: 'safety',
              text: 'Three bad calls in a week and the debrief got bumped. Peer support reached out, which helped.' },
            { id: 3, time: 'Yesterday', theme: 'Workload', sentiment: 'risk', scope: 'education',
              text: 'Class sizes went up and the support staff did not. I am doing fine at school and not fine at home.' },
            { id: 4, time: 'Yesterday', theme: 'Safety', sentiment: 'risk', scope: 'transit',
              text: 'The shields help. The thing nobody books off for is the yelling.' },
            { id: 5, time: '2 days ago', theme: 'Recognition', sentiment: 'positive', scope: 'org',
              text: 'My supervisor noticed before I said anything and moved a deadline. That is the first time.' }
        ],
        sentiment: {
            positiveSummary: 'The private check-in is read as fair, and supervisors who act on a group signal early are named with thanks.',
            emergingTheme: 'Short staffing and exposure to violence or difficult events are the most frequent sources of strain; the assistance program is known but reached late.'
        }
    },

    predictive: {
        title: 'The Mental Stress Claims Funnel',
        // Whose claims record the outlook is built on, once it is on file: the
        // forecast tab says "the board's figures", never "modeled" (2026-11-04).
        recordOwner: 'the board',
        intro: 'In the modeled baseline, a share of the high-strain cohort would be expected to register a mental stress claim within twelve months, a smaller share to be allowed with lost time, and a smaller share still to be receiving benefits a year later. This is directional, from leading signals. It is not a forecast, and it needs the board’s own claims record beside it before any causal claim.',
        lever: 'Earlier routing to support, before a claim',
        ownLabel: 'Mental stress claims per 1,000 workers',
        tiers: [
            { value: '2,400', label: 'Workers reached', source: 'observed', tone: 'neutral', width: 100 },
            { value: '290', label: 'High-strain cohort (Needs Support and Running Hot)', source: 'observed', tone: 'brand', width: 86 },
            { value: '22-34', label: 'Modeled mental stress claims registered - 12 months', source: 'modeled', tone: 'amber', width: 68 },
            { value: '12-19', label: 'Modeled allowed, with lost time', source: 'modeled', tone: 'amber', width: 52 },
            { value: '3-5', label: 'Still receiving benefits at 12 months - tail', source: 'modeled', tone: 'muted', width: 36 }
        ],
        cards: [
            { title: 'Strain-to-Claim Association', value: '~3.8x', valueTone: 'amber', level: 'Intermediate', source: 'modeled', dark: false,
              subtext: 'Modeled association - not causal',
              body: 'Sectors sustaining low capacity scores register more mental stress claims per thousand workers. In comparable modeled populations the association is roughly 3.8x: directional, and confirmed only against the board’s own record.' },
            { title: 'Modeled Registration Probability', value: '15%', valueTone: 'white', level: 'Lagging', source: 'modeled', dark: true,
              subtext: 'Midpoint - 12-18% band',
              body: 'Modeled share of the high-strain cohort that registers a mental stress claim within twelve months. The band narrows as the board’s own figures are added.' },
            { title: 'Proactive Routing Lift', value: '+31%', valueTone: 'emerald', level: 'Leading', source: 'observed', dark: false,
              subtext: 'Observed in engaged cohorts - attribution pending',
              body: 'Workers who use the check-in reach their assistance program and the supports on Talk to Someone more often than those who only read content: a leading indicator, shown as an association rather than a causal result.' }
        ],
        footer: {
            lead: 'These are models, not forecasts, and they describe sectors, never a person.',
            note: 'Projections are conservatively attributed and reported by sector only. Nothing in this console, and nothing in a member’s check-in, is used in or sent to the adjudication of any claim. Registrations, allowances and durations are lagging outcomes that need the board’s own record and a comparison baseline before any causal claim.'
        }
    },

    roi: {
        currency: 'CAD',
        locale: 'en-CA',
        benchSource: 'sector reference',
        replacementMultiple: 0.5,
        turnoverRate: 10,
        termLabels: {
            lostTime: 'Avoided Lost Time',
            lostTimeBasis: 'Baseline absence days x loaded daily cost x modeled reduction. The benefit costs of allowed claims run higher and are not booked here.'
        }
    },

    copilot: { exampleScope: 'Health Care' }
};

export default compensation;
